import { useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import type { GridColDef } from "@mui/x-data-grid";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { formatMs } from "../lib/format";
import { useI18n } from "../lib/i18n";
import type { Device, LinkCheckResult } from "../lib/types";
import { Badge, Button, Empty, ErrorBox, Field, Loading, Modal } from "./ui";
import { DataTable } from "./DataTable";
import { IpCell } from "../pages/DeviceChecks";

/** Form tạo/sửa device — dùng chung giữa trang Thiết bị và nút thêm nhanh ở Tổng quan. */
export function DeviceForm({
  device,
  onClose,
  onSaved,
}: {
  device: Device | null;
  onClose: () => void;
  /** Trả về device vừa tạo (kèm apiKey) khi tạo mới, null khi chỉ cập nhật. */
  onSaved: (created: Device | null) => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: device?.name ?? "",
    network: device?.network ?? "",
    region: device?.region ?? "",
    carrier: device?.carrier ?? "",
    model: device?.model ?? "",
    platform: device?.platform ?? "",
    appVersion: device?.appVersion ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    // Field trống gửi lên thành null thay vì chuỗi rỗng.
    const orNull = (v: string) => (v.trim() === "" ? null : v.trim());
    const payload = {
      name: form.name.trim(),
      network: orNull(form.network),
      region: orNull(form.region),
      carrier: orNull(form.carrier),
      model: orNull(form.model),
      platform: orNull(form.platform),
      appVersion: orNull(form.appVersion),
    };
    try {
      if (device) {
        await api.devices.update(device.id, payload);
        onSaved(null);
      } else {
        onSaved(await api.devices.create(payload));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={device ? t("devices.formEdit", { id: device.id }) : t("devices.formNew")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}
        <Field label={t("devices.nameLabel")} hint={t("devices.nameHint")}>
          <input value={form.name} onChange={set("name")} required />
        </Field>
        <div className="form-row">
          <Field label={t("devices.platform")}>
            <select value={form.platform} onChange={set("platform")}>
              <option value="">—</option>
              <option value="android">android</option>
              <option value="ios">ios</option>
            </select>
          </Field>
          <Field label={t("devices.model")}>
            <input value={form.model} onChange={set("model")} />
          </Field>
        </div>
        <div className="form-row">
          <Field label={t("table.network")} hint={t("devices.networkHint")}>
            <input value={form.network} onChange={set("network")} />
          </Field>
          <Field label={t("table.region")} hint={t("devices.regionHint")}>
            <input value={form.region} onChange={set("region")} />
          </Field>
        </div>
        <div className="form-row">
          <Field label={t("devices.carrier")}>
            <input value={form.carrier} onChange={set("carrier")} />
          </Field>
          <Field label={t("devices.appVersion")}>
            <input value={form.appVersion} onChange={set("appVersion")} />
          </Field>
        </div>
        <div className="form-actions">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {device ? t("devices.save") : t("devices.register")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Server chỉ trả apiKey gốc đúng một lần (lúc tạo / rotate) nên phải cho chép ngay. */
export function ApiKeyModal({ device, onClose }: { device: Device; onClose: () => void }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      title={t("devices.keyTitle")}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("devices.keyAck")}
        </Button>
      }
    >
      <div className="alert alert-warn">{t("devices.keyWarn")}</div>
      <p className="muted">
        {t("devices.keyDevice")} <strong>{device.name}</strong>
      </p>
      <div className="key-box">
        <code>{device.apiKey}</code>
        <Button
          onClick={async () => {
            await navigator.clipboard.writeText(device.apiKey!);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? t("devices.keyCopied") : t("devices.keyCopy")}
        </Button>
      </div>
      <p className="field-hint">{t("devices.keyHint")}</p>
    </Modal>
  );
}

/** Lịch sử check của riêng một máy — GET /device-checks?deviceId=… */
export function DeviceResultsModal({ device, onClose }: { device: Device; onClose: () => void }) {
  const { t, fmtDate, fmtAgo } = useI18n();
  const { data, loading, error } = useAsync(
    () => api.deviceChecks.list({ deviceId: device.id, limit: 100 }),
    [device.id],
  );
  // Kết quả chỉ có brandLinkId nên phải tra ngược sang brand/domain cho dễ đọc.
  const links = useAsync(() => api.brandLinks.list(), []);
  const rows = data ?? [];
  const linkName = (id: number) => {
    const l = (links.data ?? []).find(x => x.id === id);
    return l ? `${l.brand} · ${l.domain}` : `#${id}`;
  };

  const columns = useMemo<GridColDef<LinkCheckResult>[]>(
    () => [
      {
        field: "checkedAt",
        headerName: t("table.time"),
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Tooltip title={fmtDate(row.checkedAt)}>
            <span>{fmtAgo(row.checkedAt)}</span>
          </Tooltip>
        ),
      },
      {
        field: "brandLinkId",
        headerName: t("matrix.link"),
        flex: 1.5,
        minWidth: 170,
        valueGetter: (v: number) => linkName(v),
      },
      {
        field: "healthStatus",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 105,
        renderCell: ({ row }) => <Badge status={row.healthStatus} />,
      },
      {
        field: "statusCode",
        headerName: t("table.http"),
        type: "number",
        flex: 0.4,
        minWidth: 80,
        valueFormatter: (v: number | null) => v ?? "—",
      },
      {
        field: "responseMs",
        headerName: t("table.response"),
        type: "number",
        flex: 0.5,
        minWidth: 95,
        valueFormatter: (v: number | null) => formatMs(v),
      },
      {
        field: "region",
        headerName: t("table.region"),
        flex: 0.6,
        minWidth: 95,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "network",
        headerName: t("table.network"),
        flex: 0.5,
        minWidth: 85,
        valueGetter: (v: string | null) => v ?? "—",
      },
      {
        field: "ip",
        headerName: t("table.ip"),
        flex: 0.7,
        minWidth: 115,
        renderCell: ({ row }) => <IpCell ip={row.ip} userAgent={row.userAgent} />,
      },
      {
        field: "error",
        headerName: t("table.error"),
        flex: 0.9,
        minWidth: 100,
        valueGetter: (v: string | null) => v ?? "—",
      },
    ],
    [t, fmtDate, fmtAgo, links.data],
  );

  return (
    <Modal
      title={t("devices.resultsTitle", { name: device.name })}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t("common.close")}</Button>}
    >
      {error && <ErrorBox message={error} />}
      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty message={t("devices.resultsEmpty")} />
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          height={400}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      )}
    </Modal>
  );
}
